import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ChevronLeft, Copy, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getOptimizationsV2,
  getOptimizationsV2Source,
  setOptimizationsV2MockOverride,
  OPTIMIZATIONS_V2_SOURCES,
} from '../../services/optimizations';
import OptimizationDetailModal from '../../components/modals/OptimizationDetailModal';
import OptimizationDetailModalV2 from '../../components/modals/OptimizationDetailModalV2';
import SuggestionCard from '../../components/optimizations/SuggestionCard';
import { useAuth } from '../../components/context/AuthContext';
import { suggestionToCard } from '../../utils/optimizationTransformers';

function GlassCard({ children, className = '' }) {
  return (
    <section
      className={`backdrop-blur-xl bg-gradient-to-br from-slate-800/70 via-slate-900/70 to-indigo-900/70 border border-white/10 rounded-2xl shadow-lg ${className}`}
    >
      {children}
    </section>
  );
}

const OptimizationsV2MockInner = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const isAdmin = Boolean(auth?.user?.is_admin);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [source, setSource] = useState(() => getOptimizationsV2Source());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOptimization, setSelectedOptimization] = useState(null);
  const [currentOptimizationIndex, setCurrentOptimizationIndex] = useState(0);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setSource(getOptimizationsV2Source());
      const res = await getOptimizationsV2();
      setPayload(res);
    } catch (e) {
      setPayload(null);
      setError(e?.message || 'No se pudo cargar el mock');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectSource = useCallback((nextSource) => {
    setOptimizationsV2MockOverride(nextSource);
    setSource(nextSource);
    load();
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  const activeGroups = useMemo(() => payload?.active?.groups || [], [payload]);
  const expiredGroups = useMemo(() => payload?.expired?.groups || [], [payload]);

  const allGroupsForDetailModal = useMemo(() => {
    const toCards = (g) => (g?.suggestions || []).map((s) => suggestionToCard(s));
    return [...activeGroups, ...expiredGroups].map((g) => ({
      ruleKey: g?.rule?.key ?? null,
      cards: toCards(g),
    }));
  }, [activeGroups, expiredGroups]);

  const allOptimizations = useMemo(() => {
    return allGroupsForDetailModal.flatMap((g) => g.cards || []);
  }, [allGroupsForDetailModal]);

  const summary = useMemo(() => {
    const activeSuggestions = activeGroups.reduce((n, g) => n + (g?.suggestions?.length || 0), 0);
    const expiredSuggestions = expiredGroups.reduce((n, g) => n + (g?.suggestions?.length || 0), 0);
    const archetypes = new Set();
    activeGroups.forEach((g) => {
      (g?.suggestions || []).forEach((s) => {
        if (s?.archetype) archetypes.add(String(s.archetype));
      });
    });
    return { activeSuggestions, expiredSuggestions, archetypesCount: archetypes.size };
  }, [activeGroups, expiredGroups]);

  const onCopyJson = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      // Evitamos toast para no depender de estilos; el user ve el resultado con Ctrl+V.
    } catch (_e) {
      // ignore
    }
  }, [payload]);

  const handleOpenDetail = useCallback((groupIdx, cardIdxInGroup) => {
    const groupCards = allGroupsForDetailModal[groupIdx]?.cards || [];
    const card = groupCards[cardIdxInGroup] || null;
    if (!card) return;

    const globalIndex = allOptimizations.findIndex((c) => c?.id === card.id);
    setSelectedOptimization(card);
    setCurrentOptimizationIndex(globalIndex >= 0 ? globalIndex : 0);
    setCurrentGroupIndex(groupIdx);
    setIsModalOpen(true);
  }, [allGroupsForDetailModal, allOptimizations]);

  const handleNavigateOptimization = useCallback((newIndex) => {
    if (newIndex < 0 || newIndex >= allOptimizations.length) return;
    setSelectedOptimization(allOptimizations[newIndex] || null);
    setCurrentOptimizationIndex(newIndex);
  }, [allOptimizations]);

  const handleNavigateGroup = useCallback((newGroupIndex) => {
    if (newGroupIndex < 0 || newGroupIndex >= allGroupsForDetailModal.length) return;
    setCurrentGroupIndex(newGroupIndex);
    const groupCards = allGroupsForDetailModal[newGroupIndex]?.cards || [];
    const next = groupCards[0] || null;
    if (!next) return;
    const globalIndex = allOptimizations.findIndex((c) => c?.id === next.id);
    setSelectedOptimization(next);
    setCurrentOptimizationIndex(globalIndex >= 0 ? globalIndex : 0);
  }, [allGroupsForDetailModal, allOptimizations]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/home')}
                className="min-h-[44px] inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              >
                <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                Volver
              </button>
              <div className="text-white/60 text-sm font-semibold tracking-widest uppercase">
                dev
              </div>
            </div>
            <h1 className="mt-4 text-2xl sm:text-3xl font-semibold text-white">
              Mock — Optimizaciones v2
            </h1>
            <p className="mt-1 text-sm text-white/70">
              Fuente activa:{' '}
              <span className="font-mono text-white">
                {source === OPTIMIZATIONS_V2_SOURCES.ALL_RULES_MOCK
                  ? 'all_rules_mock (84 reglas + variantes QA)'
                  : source === OPTIMIZATIONS_V2_SOURCES.PREVIEW
                    ? 'preview (API /get-optimizations-preview)'
                    : 'api (datos reales de tu usuario)'}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={load}
              className="min-h-[44px] inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              title="Recargar"
            >
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              Recargar
            </button>
            <button
              type="button"
              onClick={onCopyJson}
              disabled={!payload}
              className="min-h-[44px] inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              title="Copiar JSON"
            >
              <Copy className="w-4 h-4" aria-hidden="true" />
              Copiar JSON
            </button>
          </div>
        </div>

        <GlassCard className="p-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs tracking-widest text-white/60 font-semibold uppercase mr-2">
              Origen de datos
            </span>
            {[
              { id: OPTIMIZATIONS_V2_SOURCES.ALL_RULES_MOCK, label: 'Mock: todas las reglas' },
              { id: OPTIMIZATIONS_V2_SOURCES.PREVIEW, label: 'Preview (API)' },
              { id: OPTIMIZATIONS_V2_SOURCES.API, label: 'API real' },
            ].map((opt) => {
              const isActive = source === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handleSelectSource(opt.id)}
                  className={`min-h-[36px] inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${
                    isActive
                      ? 'bg-indigo-500/30 border-indigo-300/50 text-white'
                      : 'bg-white/5 border-white/15 text-white/80 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
            <span className="ml-2 text-xs text-white/50">
              El override se guarda en localStorage; sobrescribe las env vars sin reiniciar el dev server.
            </span>
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          {loading ? (
            <div className="text-sm text-white/70">Cargando mock…</div>
          ) : error ? (
            <div className="text-sm text-rose-200">{error}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs tracking-widest text-white/60 font-semibold uppercase">Activas</div>
                <div className="mt-2 text-2xl font-semibold text-white tabular-nums">{activeGroups.length}</div>
                <div className="mt-1 text-sm text-white/70">{summary.activeSuggestions} suggestions</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs tracking-widest text-white/60 font-semibold uppercase">Expiradas</div>
                <div className="mt-2 text-2xl font-semibold text-white tabular-nums">{expiredGroups.length}</div>
                <div className="mt-1 text-sm text-white/70">{summary.expiredSuggestions} suggestions</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs tracking-widest text-white/60 font-semibold uppercase">Arquetipos</div>
                <div className="mt-2 text-2xl font-semibold text-white tabular-nums">{summary.archetypesCount}</div>
                <div className="mt-1 text-sm text-white/70">distintos en activas</div>
              </div>
            </div>
          )}
        </GlassCard>

        {!loading && !error && activeGroups.length > 0 ? (
          <div className="space-y-4">
            {activeGroups.map((g, groupIdx) => {
              const ruleKey = g?.rule?.key || `rule_${g?.rule?.id || 'unknown'}`;
              const suggestions = g?.suggestions || [];
              return (
                <GlassCard key={ruleKey} className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{g?.rule?.name || 'Optimización'}</div>
                      <div className="mt-1 text-xs text-white/60">
                        <span className="font-mono">{ruleKey}</span>
                        {g?.primary_kpi ? <span className="ml-2">· KPI: {g.primary_kpi}</span> : null}
                        <span className="ml-2">· {suggestions.length} suggestion{suggestions.length === 1 ? '' : 's'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 space-y-4">
                    {suggestions.map((s, cardIdx) => (
                      <div key={s.id} className="space-y-2">
                        <SuggestionCard
                          suggestion={s}
                          onApply={(_s, req) => toast.success(`PATCH ${req.path} → ${JSON.stringify(req.body)}`)}
                          onDismiss={(_s, req) => toast(`PATCH ${req.path}`)}
                        />
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => handleOpenDetail(groupIdx, cardIdx)}
                            className="rounded text-xs font-semibold text-white/55 hover:text-white underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                          >
                            Ver detalle en modal
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              );
            })}
          </div>
        ) : null}
      </div>

      {(() => {
        const DetailModalComponent = isAdmin ? OptimizationDetailModalV2 : OptimizationDetailModal;
        return (
          <DetailModalComponent
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedOptimization(null);
              setCurrentOptimizationIndex(0);
              setCurrentGroupIndex(0);
            }}
            optimization={selectedOptimization}
            allOptimizations={allOptimizations}
            currentIndex={currentOptimizationIndex}
            onNavigate={handleNavigateOptimization}
            onRatingSuccess={() => {}}
            onApplySuccess={() => {}}
            allGroups={allGroupsForDetailModal}
            currentGroupIndex={currentGroupIndex}
            onNavigateGroup={handleNavigateGroup}
          />
        );
      })()}
    </div>
  );
};

const OptimizationsV2Mock = () => {
  const { auth } = useAuth();
  if (!auth?.user?.is_admin) return <Navigate to="/home" replace />;
  return <OptimizationsV2MockInner />;
};

export default OptimizationsV2Mock;

